import { getOrInsertGlobal } from './global';
const DEFAULT_TTL = 30000; // 30 seconds

/**
 * Creates a batched early requester that can be used to make early requests through a provided batch scheduler.
 *
 * Commonly used with promisifyEarlyRequest.
 *
 * - Deduplicates early requests until they are consumed (in addition to batch-promise's built in read debouncing)
 * - Stores early requests on window in a global identified by namespace
 * - Flushes the batch scheduler when resetRequests is called
 * - Returns a promise that will resolve to the results of the batch scheduler call
 * - Rejects if the early request is not present, allowing both error handling and missing requests to flow through the same code path
 *
 * @example
 * ```ts
 * const { request, consume } = createBatchedEarlyRequester('my-namespace', scheduler);
 *
 * // In an early script, prefetch data
 * request({ objectTypeId: '0-1', objectId: '1' });
 * request({ objectTypeId: '0-1', objectId: '2' });
 *
 * // Later, in a resolver, consume the prefetched result (or fall back to a fresh fetch)
 * const result = await consume({ objectTypeId: '0-1', objectId: '1' })
 *   .catch(() => fetchFresh(objectTypeId, objectId));
 * ```
 *
 *
 * @param namespace A unique string used to store batches on window
 * @param batchScheduler A batch scheduler instance that executes the early request
 * @returns An object with `request`, `consume`, and `__TEST_ONLY_reset` functions
 */
export const createBatchedEarlyRequester = (namespace, batchScheduler) => {
  const requests = getOrInsertGlobal(namespace, () => new Map());
  const requestMeta = new WeakMap();
  let ttlSweepInterval = null;
  const stopSweep = () => {
    if (ttlSweepInterval !== null) {
      clearInterval(ttlSweepInterval);
      ttlSweepInterval = null;
    }
  };
  const startSweep = () => {
    if (ttlSweepInterval !== null) {
      return;
    }
    ttlSweepInterval = setInterval(() => {
      const now = Date.now();
      for (const [identity, promise] of requests) {
        const meta = requestMeta.get(promise);
        if (meta && meta.settled && now > meta.requestedAt + DEFAULT_TTL) {
          requests.delete(identity);
        }
      }
      if (requests.size === 0) {
        stopSweep();
      }
    }, DEFAULT_TTL / 2);
  };
  return {
    request: call => {
      const identity = batchScheduler.identity(call);
      if (requests.has(identity)) {
        return requests.get(identity);
      }
      const promise = batchScheduler.queue(call);
      requests.set(identity, promise);
      const meta = {
        requestedAt: Date.now(),
        settled: false
      };
      requestMeta.set(promise, meta);
      // Make unhandled rejection tooling happy
      promise.then(() => {
        meta.settled = true;
      }).catch(() => {
        meta.settled = true;
      });
      startSweep();
      return promise;
    },
    consume: call => {
      const identity = batchScheduler.identity(call);
      const promise = requests.get(identity);
      if (promise) {
        requests.delete(identity);
        return promise;
      }
      return Promise.reject(new Error(`Early request not found: ${identity}`));
    },
    __TEST_ONLY_reset: () => {
      requests.clear();
      stopSweep();
      return batchScheduler.abort();
    }
  };
};